// =====================================================================
// uiLuat.ts — luật thuần cho component dùng chung (N3): tìm không dấu · sắp xếp 3 trạng thái (QĐ-H69) ·
// ô ngày dd/mm/yyyy (QĐ-H78) · định vị popover không tràn màn hình. Không React, không mạng.
// =====================================================================

/** "Nguyễn Thị Đào" ⇒ "nguyen thi dao" — tìm kiếm không phân biệt dấu / hoa thường. */
export function chuKhongDau(s: string | null | undefined): string {
  return (s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d").replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Mọi từ của ô tìm đều phải có trong ít nhất 1 trường (không dấu). Ô tìm trống ⇒ khớp. */
export function khopTim(oTim: string, ...truong: Array<string | null | undefined>): boolean {
  const tu = chuKhongDau(oTim).split(" ").filter(Boolean);
  if (!tu.length) return true;
  const goc = truong.map(chuKhongDau).join(" | ");
  return tu.every((t) => goc.includes(t));
}

// ---------------------------------------------------------------------
// Sắp xếp cột 3 trạng thái: bấm 1 tăng → 2 giảm → 3 bỏ (QĐ-H69). Bấm cột khác ⇒ tăng dần cột đó.
// ---------------------------------------------------------------------
export interface SapXep { khoa: string; chieu: "tang" | "giam" }

export function vongSapXep(hienTai: SapXep | null, khoa: string): SapXep | null {
  if (!hienTai || hienTai.khoa !== khoa) return { khoa, chieu: "tang" };
  if (hienTai.chieu === "tang") return { khoa, chieu: "giam" };
  return null;
}

const viColl = new Intl.Collator("vi", { numeric: true, sensitivity: "base" });

/** So 2 giá trị ô: số theo số, chữ theo tiếng Việt (có số tự nhiên: "Lớp 2" < "Lớp 10"). Trống luôn xếp CUỐI dù tăng hay giảm. */
export function soSanhGiaTri(a: unknown, b: unknown, chieu: "tang" | "giam"): number {
  const trongA = a === null || a === undefined || a === "";
  const trongB = b === null || b === undefined || b === "";
  if (trongA || trongB) return trongA === trongB ? 0 : trongA ? 1 : -1;
  let kq: number;
  if (typeof a === "number" && typeof b === "number") kq = a - b;
  else if (typeof a === "boolean" && typeof b === "boolean") kq = Number(a) - Number(b);
  else kq = viColl.compare(String(a), String(b));
  return chieu === "tang" ? kq : -kq;
}

/** Sắp ổn định (giữ thứ tự gốc khi bằng nhau). Không sắp ⇒ trả lại đúng mảng gốc. */
export function sapXepDong<T>(dong: readonly T[], sx: SapXep | null, layGiaTri: (d: T, khoa: string) => unknown): readonly T[] {
  if (!sx) return dong;
  return dong
    .map((d, i) => ({ d, i, v: layGiaTri(d, sx.khoa) }))
    .sort((x, y) => soSanhGiaTri(x.v, y.v, sx.chieu) || x.i - y.i)
    .map((x) => x.d);
}

// ---------------------------------------------------------------------
// Ô ngày tự làm: luôn hiện dd/mm/yyyy, không phụ thuộc ngôn ngữ trình duyệt (QĐ-H78).
// ---------------------------------------------------------------------
const pad2 = (n: number) => String(n).padStart(2, "0");

/** 'YYYY-MM-DD' ⇒ 'dd/mm/yyyy'. Sai ⇒ ''. */
export function isoSangONgay(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * Chữ người gõ ⇒ 'YYYY-MM-DD'. Nhận: 'd/m/yyyy', 'dd/mm/yyyy', 'd-m-yyyy', 'd.m.yyyy', 'ddmmyyyy', 'd/m/yy' (yy ⇒ 20yy).
 * Ngày không có thật (31/02, 29/02 năm thường) ⇒ null — KHÔNG tự cộng tràn như new Date.
 * Trống ⇒ '' (người dùng xoá ngày). Không đọc được ⇒ null.
 */
export function docONgay(chu: string): string | null {
  const s = chu.trim();
  if (!s) return "";
  let d: number, m: number, y: number;
  const tach = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  const lien = /^(\d{2})(\d{2})(\d{4})$/.exec(s);
  if (tach) { d = +tach[1]; m = +tach[2]; y = +tach[3]; if (tach[3].length === 2) y += 2000; }
  else if (lien) { d = +lien[1]; m = +lien[2]; y = +lien[3]; }
  else return null;
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1) return null;
  const soNgay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d > soNgay) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

// ---------------------------------------------------------------------
// Popover: đặt dưới mốc (căn trái); tràn đáy ⇒ lật lên trên; tràn phải ⇒ căn mép phải; luôn cách mép 8 px.
// ---------------------------------------------------------------------
export interface HinhChuNhat { top: number; left: number; bottom: number; right: number; width: number; height: number }

export function viTriPopover(moc: HinhChuNhat, w: number, h: number, manW: number, manH: number, canPhai = false): { top: number; left: number } {
  const LE = 8, CACH = 4;
  let left = canPhai ? moc.right - w : moc.left;
  left = Math.min(left, manW - w - LE);
  left = Math.max(LE, left);
  let top = moc.bottom + CACH;
  if (top + h > manH - LE) {
    const tren = moc.top - CACH - h;
    top = tren >= LE ? tren : Math.max(LE, manH - LE - h);
  }
  return { top: Math.round(top), left: Math.round(left) };
}

/** Chữ trên nút dropdown chọn nhiều: trống ⇒ nhãn mặc định ("Tất cả khu vực"); 1 mục ⇒ tên mục; nhiều ⇒ "3 đã chọn". */
export function chuTomTat(daChon: readonly string[], luaChon: readonly { giaTri: string; nhan: string }[], nhanTrong: string): string {
  if (!daChon.length) return nhanTrong;
  if (daChon.length === 1) return luaChon.find((l) => l.giaTri === daChon[0])?.nhan ?? daChon[0];
  return `${daChon.length} đã chọn`;
}
